import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useSoundPreview } from '@features/Admin/AmbienceSettings/hooks/useSoundPreview';
import { clampPercentage } from '@features/Admin/AmbienceSettings/utils/ambienceSettings.utils';
import { ConfirmDialog } from '@shared/components/confirmDialog/ConfirmDialog';
import { CustomSuccessToast } from '@shared/components/customSuccessToast';
import { useErrorHandler } from '@shared/hooks/useErrorHandler';
import {
  BackgroundSoundResponseDto,
  useBackgroundSoundCreateControllerCreateMutation,
  useBackgroundSoundDeleteControllerDeleteMutation,
  useBackgroundSoundListControllerAdminListQuery,
  useBackgroundSoundUpdateControllerUpdateMutation,
  useBackgroundSoundUploadUrlControllerCreateUploadUrlMutation,
} from '@shared/services/apiService/apiService';
import AmbienceSoundDialog from './AmbienceSoundDialog';
import {
  buildCreatePayload,
  buildUploadUrlPayload,
  buildUpdatePayload,
  createEditFormState,
  FormErrors,
  getSelectedFileName,
  getUploadMimeType,
  initialFormState,
  SoundFormState,
  SoundFileValidationError,
  validateSoundFile,
} from './ambienceSoundForm.utils';
import { useStyles } from './styles';

const getSoundTypeLabel = (
  value: string | undefined,
  t: ReturnType<typeof useTranslation>['t'],
) => {
  switch (value) {
    case 'ambient':
      return t('superAdminManageAmbienceSounds.table.typeAmbient');
    case 'thinking':
      return t('superAdminManageAmbienceSounds.table.typeThinking');
    default:
      return value ?? '';
  }
};

export const ManageAmbienceSoundsPage: React.FC = () => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { showError } = useErrorHandler();
  const { playingPreviewKeys, stopPreview, togglePreview, updatePreviewVolume } = useSoundPreview();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedSound, setSelectedSound] = useState<BackgroundSoundResponseDto | null>(null);
  const [form, setForm] = useState<SoundFormState>(initialFormState);
  const [errors, setErrors] = useState<FormErrors>({});
  const [draftPreviewUrl, setDraftPreviewUrl] = useState<string | null>(null);

  const {
    data: sounds,
    isLoading,
    isError,
    refetch,
  } = useBackgroundSoundListControllerAdminListQuery();
  const [createSound, { isLoading: isCreating }] =
    useBackgroundSoundCreateControllerCreateMutation();
  const [updateSound, { isLoading: isUpdating }] =
    useBackgroundSoundUpdateControllerUpdateMutation();
  const [createUploadUrl, { isLoading: isPreparingUpload }] =
    useBackgroundSoundUploadUrlControllerCreateUploadUrlMutation();
  const [deleteSound] = useBackgroundSoundDeleteControllerDeleteMutation();

  const isEditMode = Boolean(selectedSound);
  const isSaving = isCreating || isUpdating || isPreparingUpload;
  const isReplaceFileDisabled = Boolean(selectedSound?.isDefault);
  const dialogPreviewKey = 'ambience-sound-form-preview';

  const getFileErrorMessage = (reason: SoundFileValidationError) => {
    switch (reason) {
      case 'file_too_large':
        return t('superAdminManageAmbienceSounds.form.fileTooLarge');
      case 'invalid_type':
      default:
        return t('superAdminManageAmbienceSounds.form.fileInvalidType');
    }
  };

  useEffect(() => {
    if (!form.file) {
      setDraftPreviewUrl((prev) => {
        if (prev) {
          URL.revokeObjectURL(prev);
        }

        return null;
      });
      return;
    }

    const objectUrl = URL.createObjectURL(form.file);
    setDraftPreviewUrl((prev) => {
      if (prev) {
        URL.revokeObjectURL(prev);
      }

      return objectUrl;
    });

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [form.file]);

  const selectedFileName = useMemo(() => {
    return getSelectedFileName(form, selectedSound, {
      keepExistingFile: t('superAdminManageAmbienceSounds.form.keepExistingFile'),
      noFileSelected: t('superAdminManageAmbienceSounds.form.noFileSelected'),
    });
  }, [form.file, selectedSound, t]);

  const dialogTitle = isEditMode
    ? t('superAdminManageAmbienceSounds.editTitle')
    : t('superAdminManageAmbienceSounds.createTitle');
  const previewUrl = draftPreviewUrl ?? selectedSound?.fileUrl;
  const isPreviewPlaying = Boolean(playingPreviewKeys[dialogPreviewKey]);

  const resetDialogState = () => {
    stopPreview(dialogPreviewKey);
    setDialogOpen(false);
    setSelectedSound(null);
    setForm(initialFormState);
    setErrors({});
    setDraftPreviewUrl((prev) => {
      if (prev) {
        URL.revokeObjectURL(prev);
      }

      return null;
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCreateClick = () => {
    setSelectedSound(null);
    setForm(initialFormState);
    setErrors({});
    setDialogOpen(true);
  };

  const handleEditClick = (sound: BackgroundSoundResponseDto) => {
    setSelectedSound(sound);
    setForm(createEditFormState(sound));
    setErrors({});
    setDialogOpen(true);
  };

  const handleDeleteClick = (sound: BackgroundSoundResponseDto) => {
    if (sound.isDefault) {
      return;
    }
    setSelectedSound(sound);
    setDeleteOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteOpen(false);
    setSelectedSound(null);
  };

  const updateField = <K extends keyof SoundFormState>(key: K, value: SoundFormState[K]) => {
    if (key === 'file') {
      const nextFile = value as SoundFormState['file'];
      const validationError = validateSoundFile(nextFile);

      if (validationError) {
        setForm((prev) => ({ ...prev, file: null }));
        setErrors((prev) => ({ ...prev, file: getFileErrorMessage(validationError) }));

        return;
      }
    }

    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validateForm = () => {
    const nextErrors: FormErrors = {};
    const trimmedName = form.name.trim();

    if (!trimmedName) {
      nextErrors.name = t('superAdminManageAmbienceSounds.form.nameRequired');
    }

    if (!isEditMode && !form.file) {
      nextErrors.file = t('superAdminManageAmbienceSounds.form.fileRequired');
    }

    if (form.file) {
      const fileValidationError = validateSoundFile(form.file);

      if (fileValidationError) {
        nextErrors.file = getFileErrorMessage(fileValidationError);
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const showSuccessToast = (title: string, description: string) => {
    toast.success(CustomSuccessToast, {
      closeButton: false,
      autoClose: 5000,
      icon: false,
      data: { title, description },
    });
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const uploadFile = async (file: File) => {
        const { uploadUrl, uploadKey } = await createUploadUrl({
          backgroundSoundUploadUrlRequestDto: buildUploadUrlPayload(form.type, file),
        }).unwrap();

        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': getUploadMimeType(file),
          },
        });

        if (!uploadResponse.ok) {
          throw new Error('Background sound upload failed');
        }

        return uploadKey;
      };

      if (selectedSound) {
        const uploadKey = form.file ? await uploadFile(form.file) : undefined;

        await updateSound({
          id: selectedSound.id,
          backgroundSoundUpdateRequestDto: buildUpdatePayload(form, uploadKey),
        }).unwrap();
        showSuccessToast(
          t('superAdminManageAmbienceSounds.soundUpdatedTitle'),
          t('superAdminManageAmbienceSounds.soundUpdatedDescription', {
            name: form.name.trim(),
          }),
        );
      } else {
        const uploadKey = await uploadFile(form.file as File);

        await createSound({
          backgroundSoundCreateRequestDto: buildCreatePayload(form, uploadKey),
        }).unwrap();
        showSuccessToast(
          t('superAdminManageAmbienceSounds.soundCreatedTitle'),
          t('superAdminManageAmbienceSounds.soundCreatedDescription', {
            name: form.name.trim(),
          }),
        );
      }

      await refetch();
      resetDialogState();
    } catch (error) {
      showError({
        title: isEditMode
          ? t('superAdminManageAmbienceSounds.errors.updateTitle')
          : t('superAdminManageAmbienceSounds.errors.createTitle'),
        error,
        suggestion: isEditMode
          ? t('superAdminManageAmbienceSounds.errors.updateSuggestion')
          : t('superAdminManageAmbienceSounds.errors.createSuggestion'),
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedSound || selectedSound.isDefault) {
      return;
    }

    try {
      await deleteSound({ id: selectedSound.id }).unwrap();
      showSuccessToast(
        t('superAdminManageAmbienceSounds.soundRemovedTitle'),
        t('superAdminManageAmbienceSounds.soundRemovedDescription', {
          name: selectedSound.name,
        }),
      );
      await refetch();
      handleDeleteClose();
    } catch (error) {
      showError({
        title: t('superAdminManageAmbienceSounds.errors.deleteTitle'),
        error,
        suggestion: t('superAdminManageAmbienceSounds.errors.deleteSuggestion'),
      });
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box className={classes.page}>
        <Box className={classes.header}>
          <Typography variant="h5" fontWeight={600}>
            {t('superAdminManageAmbienceSounds.title')}
          </Typography>
          <Button variant="contained" onClick={handleCreateClick}>
            {t('superAdminManageAmbienceSounds.create')}
          </Button>
        </Box>
        <Box className={classes.errorState}>
          <Typography variant="h6">{t('superAdminManageAmbienceSounds.errorTitle')}</Typography>
          <Typography color="textSecondary">
            {t('superAdminManageAmbienceSounds.errorMessage')}
          </Typography>
          <Button variant="outlined" onClick={() => refetch()}>
            {t('superAdminManageAmbienceSounds.retry')}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.page}>
      <Box className={classes.header}>
        <Typography variant="h5" fontWeight={600}>
          {t('superAdminManageAmbienceSounds.title')}
        </Typography>
        <Button variant="contained" onClick={handleCreateClick}>
          {t('superAdminManageAmbienceSounds.create')}
        </Button>
      </Box>

      {!sounds?.length ? (
        <Box>
          <Typography color="textSecondary">{t('superAdminManageAmbienceSounds.empty')}</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableHeadCell}>
                  {t('superAdminManageAmbienceSounds.table.sound')}
                </TableCell>
                <TableCell className={classes.tableHeadCell}>
                  {t('superAdminManageAmbienceSounds.table.type')}
                </TableCell>
                <TableCell className={classes.tableHeadCell}>
                  {t('superAdminManageAmbienceSounds.table.defaultVolume')}
                </TableCell>
                <TableCell className={classes.tableHeadCell}>
                  {t('superAdminManageAmbienceSounds.table.preview')}
                </TableCell>
                <TableCell className={classes.tableHeadCell}>
                  {t('superAdminManageAmbienceSounds.table.actions')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sounds.map((sound) => (
                <TableRow key={sound.id} className={classes.tableRow}>
                  <TableCell className={classes.tableCell}>
                    <Typography variant="body1" color="textPrimary">
                      {sound.name}
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Typography variant="body1" color="textPrimary">
                      {getSoundTypeLabel(sound.type, t)}
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Typography variant="body1" color="textPrimary">
                      {sound.defaultVolume}
                    </Typography>
                  </TableCell>
                  <TableCell className={`${classes.tableCell} ${classes.audioCell}`}>
                    {sound.fileUrl ? (
                      <audio
                        controls
                        controlsList="nodownload noplaybackrate"
                        aria-label={t('superAdminManageAmbienceSounds.previewLabel', {
                          name: sound.name,
                        })}>
                        <source src={sound.fileUrl} />
                        {t('superAdminManageAmbienceSounds.previewFallback')}
                      </audio>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        {t('superAdminManageAmbienceSounds.previewFallback')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell className={`${classes.tableCell} ${classes.actionsCell}`}>
                    <Button onClick={() => handleEditClick(sound)} sx={{ mr: 1 }}>
                      {t('common.edit')}
                    </Button>
                    <Button
                      color="error"
                      onClick={() => handleDeleteClick(sound)}
                      disabled={sound.isDefault}>
                      {t('common.delete')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <AmbienceSoundDialog
        open={dialogOpen}
        isEditMode={isEditMode}
        isSaving={isSaving}
        isReplaceFileDisabled={isReplaceFileDisabled}
        title={dialogTitle}
        form={form}
        errors={errors}
        selectedFileName={selectedFileName}
        previewUrl={previewUrl}
        isPreviewPlaying={isPreviewPlaying}
        fileInputRef={fileInputRef}
        onClose={resetDialogState}
        onSave={handleSave}
        onFieldChange={(key, value) => {
          if (key === 'defaultVolume') {
            updatePreviewVolume(dialogPreviewKey, clampPercentage(value as number));
          }

          updateField(key, value);
        }}
        onPreviewToggle={() =>
          togglePreview(dialogPreviewKey, previewUrl, clampPercentage(form.defaultVolume))
        }
      />

      <ConfirmDialog
        showDialogFlag={deleteOpen}
        handleClose={() => handleDeleteClose()}
        showDialog={setDeleteOpen}
        confirmDialog={handleDeleteConfirm}
        dialogTitle="superAdminManageAmbienceSounds.confirmDeleteTitle"
        dialogContentText={t('superAdminManageAmbienceSounds.deleteConfirmation', {
          name: selectedSound?.name ?? '',
        })}
        variant="warning"
      />
    </Box>
  );
};
